// The nightly books check on Books › Overview (books.integrityStatus): one line when it passed,
// each broken rule in words with its documents and where to look when not, and ServiceTitan's own
// figures on a quieter line of their own. The check only reports; nothing here changes anything.

import { Fragment } from 'react';
import { DocLink } from '../shared/DocLink.tsx';
import type { BooksProps } from './props.ts';
import { seesPayroll } from './model.ts';
import { integrityView, type IntegrityRow, type IntegrityStatus } from './integrity-model.ts';
import { useQuery } from './ui.tsx';

/** "INV-1042, INV-1043 and 2 more", each number a link to its document when it has one. */
function Names({ shown, more }: { shown: IntegrityRow[]; more: number }) {
  return (
    <>
      {shown.map((r, i) => (
        <Fragment key={r.key}>
          {i > 0 && ', '}
          <DocLink doc={r.doc}>{r.label}</DocLink>
        </Fragment>
      ))}
      {more > 0 && ` and ${more} more`}
    </>
  );
}

export function IntegrityLine({ go, timeZone, held }: Pick<BooksProps, 'go' | 'timeZone' | 'held'>) {
  const q = useQuery<IntegrityStatus | null>('books.integrityStatus', {});
  // Before it loads, or for a role that can't read the books, say nothing.
  if (q.data === undefined) return null;
  const v = integrityView(
    q.data,
    timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    new Date(),
    (part) => part !== 'payroll' || seesPayroll(held),
  );
  return (
    <section className="integrity" aria-label="Nightly books check">
      <p className={`notice${v.tone === 'none' ? '' : ` ${v.tone}`}`}>{v.head}</p>
      {v.problems.length > 0 && (
        <ul className="todo">
          {v.problems.map((p) => {
            // Its documents link to themselves; a rule without one opens the Books part to look in.
            const linked = p.shown.some((r) => r.doc);
            const body = (
              <span>
                <strong>{p.says}</strong>
                <span className="muted small">
                  <Names shown={p.shown} more={p.more} />
                  {p.go && !linked ? ` · in ${p.go.label}` : ''}
                </span>
              </span>
            );
            return (
              <li key={p.id}>
                {p.go && !linked ? (
                  <button className="todo-row" onClick={() => go(p.go!.part)}>
                    {body}
                    <span aria-hidden="true">›</span>
                  </button>
                ) : (
                  <div className="todo-row static">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {v.alsoUnfinished && <p className="notice warn">{v.alsoUnfinished}</p>}
      {v.importedCount > 0 && (
        <details className="imported">
          <summary>
            {v.importedCount === 1 ? '1 imported record keeps' : `${v.importedCount} imported records keep`}{' '}
            ServiceTitan’s own figures, for the migration’s reconciliation
          </summary>
          <ul>
            {v.imported.map((i) => (
              <li key={i.title}>
                {i.title}: <Names shown={i.shown} more={i.more} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
